import { ColumnDef, Switch, useModal } from "@commonlibs";
import type { LocalGiftCardDto } from "common/models/api/models/LocalGiftCardDto";
import useTranslate from "ui/locale/hooks/useTranslate";
import ActionsMenu from "ui/common/components/input/ActionsMenu/ActionsMenu";
import { useNavigate } from "react-router-dom";
import { MainRoutes } from "ui/common/router/MainRouter.types";
import TableImage from "ui/common/components/display/DataTable/TableImage/TableImage";
import EyeIcon from "ui/common/icons/EyeIcon";
import File2Icon from "ui/common/icons/File2Icon";
import GiftCardModal from "../containers/GiftCardForm/GiftCardFormModal";
import { convert24HourTo12Hour } from "ui/common/utils/dateUtils";
import GiftCardTermsAndConditionsModal from "../containers/GiftCardsTable/GiftCardTermsAndConditionsModal";
import GiftCardValuesModal from "../containers/GiftCardsTable/GiftCardValuesModal";
import GiftcardVoucherModal from "../containers/GiftcardVoucherModal";
import DeleteGiftCardModal from "../containers/DeleteGiftCardModal";
import useUpdateLocalGiftCardStatusMutation from "features/giftcards/hooks/useUpdateGiftCardStatus";
import useUserState from "ui/adapter/state/useUserState";
import { RoleType } from "common/models/role/Role";
import { MenuAction } from "ui/common/components/input/ActionsMenu/ActionsMenu.types";
import { GiftCardStockStatus } from "common/models/api/models/GiftCardStock";

export enum GIFTCARDS_COLUMNS {
    GIFTCARD_IMAGE = "imageUrl",
    PARTNER_NAME = "name",
    CATEGORY = "category",
    TERMS_AND_CONDITIONS = "terms",
    VOUCHER = "voucher",
    GIFTCAD_VALUE = "releaserEmail",
    CLOSING_TIME = "",
    AGREEMENT_CODE = "agreementCode",
    ACTIONS = "actions",
    STATUS = "status",
}

export const GIFTCARDS_COLUMNS_KEYS = Object.values(GIFTCARDS_COLUMNS);

export function useGiftCardsColumns(): ColumnDef<LocalGiftCardDto>[] {
    const translate = useTranslate();
    const { showModal } = useModal();
    const navigate = useNavigate();
    const { mutate: updateGiftCardStatus } = useUpdateLocalGiftCardStatusMutation({});
    const { selectedRole } = useUserState();
    const isCustomerService = selectedRole?.roleType === RoleType.CUSTOMER_SERVICE;

    return [
        {
            accessorKey: GIFTCARDS_COLUMNS.GIFTCARD_IMAGE,
            header: translate("giftCardImage"),
            enableSorting: false,
            size: 120,
            align: "center",
            alignCells: "center",
            cell: (info) => info.getValue(),
            accessorFn: (row) => <TableImage src={row.imageUrl || ""} alt={`${row.name}-image`} />,
        },
        {
            accessorKey: GIFTCARDS_COLUMNS.PARTNER_NAME,
            header: translate("giftCardPartnerName"),
            enableSorting: false,
            size: 150,
            align: "left",
            alignCells: "left",
            accessorFn: (row) => row.partner.name,
        },
        {
            accessorKey: GIFTCARDS_COLUMNS.CATEGORY,
            header: translate("category"),
            enableSorting: false,
            size: 150,
            align: "center",
            alignCells: "center",
            accessorFn: (row) => row.category,
        },
        {
            accessorKey: GIFTCARDS_COLUMNS.STATUS,
            header: translate("status"),
            enableSorting: false,
            size: 150,
            align: "center",
            alignCells: "center",
            cell: (info) => info.getValue(),
            accessorFn: (row) => (
                <div style={{ display: "flex", justifyContent: "center" }}>
                    {isCustomerService ? (
                        <span>{translate(row.isActive ? "active" : "inactive")}</span>
                    ) : (
                        <Switch
                            value={row.isActive}
                            onChange={() => updateGiftCardStatus({ id: row.id.toString(), is_active: !row.isActive })}
                        />
                    )}
                </div>
            ),
        },
        {
            accessorKey: GIFTCARDS_COLUMNS.TERMS_AND_CONDITIONS,
            enableSorting: false,
            id: "terms-and-conditions-column",
            header: () => (
                <>
                    Términos
                    <br />y condiciones
                </>
            ),
            alignHeader: "center",
            align: "center",
            cell: (info) => info.getValue(),
            accessorFn: (row) => (
                <EyeIcon
                    onClick={() =>
                        showModal(GiftCardTermsAndConditionsModal, {
                            giftCard: row,
                        })
                    }
                    css={{ cursor: "pointer" }}
                    fontSize={20}
                />
            ),
        },
        {
            accessorKey: GIFTCARDS_COLUMNS.VOUCHER,
            enableSorting: false,
            id: "voucher",
            header: translate("giftCardVoucherModel"),
            alignHeader: "center",
            align: "center",
            cell: (info) => info.getValue(),
            accessorFn: (row) => (
                <File2Icon
                    onClick={() =>
                        showModal(GiftcardVoucherModal, {
                            giftCard: row,
                        })
                    }
                    css={{ cursor: "pointer" }}
                    fontSize={16}
                    viewBox="0 0 34 34"
                />
            ),
        },
        {
            accessorKey: GIFTCARDS_COLUMNS.GIFTCAD_VALUE,
            header: translate("giftCardValue"),
            enableSorting: false,
            size: 150,
            alignHeader: "center",
            align: "center",
            alignCells: "center",
            cell: (info) => info.getValue(),
            accessorFn: (row) => (
                <EyeIcon
                    onClick={() => {
                        if (row.type === GiftCardStockStatus.stock) {
                            navigate(`${MainRoutes.GIFTCARDS}?type=stock&partner=${row.partner.address}`);
                        } else {
                            showModal(GiftCardValuesModal, {
                                giftCard: row,
                            })
                        }
                    }}
                    css={{ cursor: "pointer" }}
                    fontSize={20}
                />
            ),
        },
        {
            accessorKey: GIFTCARDS_COLUMNS.CLOSING_TIME,
            header: translate("closingTime"),
            enableSorting: false,
            size: 150,
            alignHeader: "center",
            align: "center",
            alignCells: "center",
            accessorFn: (row) => {
                return row.closingTime ? convert24HourTo12Hour(row.closingTime) : "N/A";
            },
        },
        {
            accessorKey: GIFTCARDS_COLUMNS.AGREEMENT_CODE,
            header: translate("agreementCode"),
            enableSorting: false,
            size: 150,
            alignHeader: "center",
            align: "center",
            alignCells: "center",
            accessorFn: (row) => {
                return row.agreementCode || "N/A";
            },
        },
        {
            accessorKey: GIFTCARDS_COLUMNS.ACTIONS,
            header: () => "",
            size: 60,
            cell: (info) => info.getValue(),
            accessorFn: (row) => {
                const actions: MenuAction[] = [
                    {
                        label: "viewTransactions",
                        onClick: () => navigate(`${MainRoutes.TRANSACTIONS}?address=${row.partner.address}`),
                    },
                ];

                if (!isCustomerService) {
                    actions.unshift({
                        label: "edit",
                        onClick: () => {
                            showModal(GiftCardModal, { giftCard: row });
                        },
                    });
                    actions.push({
                        label: "deleteGiftCard",
                        onClick: () => showModal(DeleteGiftCardModal, { giftCard: row }),
                    });
                }

                return <ActionsMenu actions={actions} />;
            },
            enableSorting: false,
        },
    ];
}
